import React from 'react';

import Button from '../../../Form/Button/Button';
import Backdrop from '../../../Modal/Backdrop';

import { AccountContext } from '../../../Context/AccountContext';
import { ActionType, CustomerCarts, ShopwareCartWithMessages } from '../../Types';
import CartItem from '../CartItem/CartItem';
import CartNotificationAdd from '../CartNotifications/CartNotificationAdd';
import CartNotificationFailure from '../CartNotifications/CartNotificationFailure';
import CartNotificationUpdate from '../CartNotifications/CartNotificationUpdate';
import Total from './Total';
import { CartMessages } from '@common/CartMessages/CartMessages';

import '../Cart.scss';

interface Props {
    cart: ShopwareCartWithMessages;
    hideCart: () => void;
    latestAction: ActionType | null;
    syncFailure: boolean;
}

const ShopwareCart: React.FunctionComponent<Props> = (props) => {
    const { accountData, translateIdToName } = React.useContext(AccountContext);
    const carts = props.cart.carts;

    // This should never happen, because we load a different  LocalCart for logged out users
    if (!accountData) {
        throw new Error('can not render component without account data');
    }

    const toggleVisibilityClass = () => {
        if (props.cart.visible) {
            return 'slide-in-cart--visible';
        }

        return '';
    };

    const cartsWithContent = (): number => {
        if (!carts) {
            return 0;
        }

        return Object.values(carts).filter((cart) => cart.items.length > 0).length;
    };

    let cartsWithSortedItems: CustomerCarts | null = null;

    if (carts) {
        cartsWithSortedItems = Object.fromEntries(
            Object.entries(carts).map(([customerId, customerData]) => {
                return [
                    customerId,
                    {
                        ...customerData,
                        items: customerData.items.sort((a, b) => a.articleNumber.localeCompare(b.articleNumber)),
                    },
                ];
            }),
        );
    }

    return (
        <>
            <aside className={'slide-in-cart ' + toggleVisibilityClass()}>
                <CartNotificationAdd latestAction={props.latestAction} />
                <CartNotificationUpdate latestAction={props.latestAction} />
                <CartNotificationFailure syncFailure={props.syncFailure} />
                <div className="cart">
                    <button
                        className="slide-in-cart__close-button"
                        onClick={props.hideCart}
                        aria-label="Warenkorb schließen"
                    >
                        <svg className="icon icon--arrow">
                            <use xlinkHref="#icon-arrow" />
                        </svg>
                        Weiter einkaufen
                    </button>
                    <CartMessages cartMessages={props.cart.cartMessages} />
                    {cartsWithSortedItems && cartsWithContent() > 0 ? (
                        <>
                            <ul>
                                {Object.entries(cartsWithSortedItems).map(([customerId, customerData]) => {
                                    if (customerData.items.length === 0) {
                                        return null;
                                    }

                                    // only show name if there are more than one customer carts
                                    // or if a single cart is not the main account
                                    const name =
                                        cartsWithContent() > 1 || customerId !== accountData.id
                                            ? translateIdToName(customerId)
                                            : null;

                                    return (
                                        <li key={customerId} className="slide-in-cart__section">
                                            {name ? (
                                                <h3 className="slide-in-cart__section-headline">{name.name}</h3>
                                            ) : (
                                                ''
                                            )}
                                            <div>
                                                {customerData.items.map((cartItem, index) => {
                                                    return (
                                                        <CartItem item={cartItem} key={index} customerId={customerId} />
                                                    );
                                                })}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                            <Total carts={cartsWithSortedItems} />
                            <Button
                                type="button"
                                className="button--color-petrol"
                                primary={true}
                                onClick={(event) => (window.location.href = window.__GGShopUrl + '/at/checkout')}
                            >
                                zur Kasse
                            </Button>

                            <Button
                                type="button"
                                className="button--color-petrol"
                                onClick={(event) => (window.location.href = window.__GGShopUrl + '/at/checkout/cart')}
                            >
                                Warenkorb bearbeiten
                            </Button>
                        </>
                    ) : (
                        <p> Sie haben noch keine Produkte im Warenkorb</p>
                    )}
                </div>
            </aside>
            <Backdrop visible={props.cart.visible} onClick={props.hideCart} />
        </>
    );
};

export default ShopwareCart;
